import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Input,
  IconButton,
  Button,
  Select,
  Tooltip,
  HStack,
  VStack,
  Flex,
  Text,
  Stack,
  Card,
  CardBody,
  Link as ChakraLink
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { FaChevronUp, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import { RiQuestionnaireLine } from 'react-icons/ri';
import { BsChevronDoubleUp } from 'react-icons/bs';
import { useTranslation } from 'react-i18next';
import DebouncedInput from '../../components/DebouncedInput';
import { FAQItem } from '../../contexts/types';
// データ読み込み
import FaqData from './faq.deta.json';


export const Faq: React.FC = () => {
  /** コンポーネントが描画されたタイミングでタブのタイトルを変更 */
  useEffect(() => {
    document.title = 'FAQ | 設備投資システム';
  }, []);

  /** 選択言語取得 */
  const { t, i18n } = useTranslation();
  /** supportedLngs は 'ja' / 'en' の2値のみのため、それ以外は 'ja' 扱いにする */
  const lang: 'ja' | 'en' = i18n.language === 'en' ? 'en' : 'ja';
  /** FAQデータの取得(jsonから) */
  const faqData: FAQItem[] = Array.isArray(FaqData.item) ? (FaqData.item as FAQItem[]) : [];

  /** 検索  */
  const [searchQuery, setSearchQuery] = useState('');
  /** カテゴリ選択 */
  const [selectedCategory, setSelectedCategory] = useState(t('features.faq.label.all'));
  /** サブカテゴリ選択 */
  const [selectedSubCategory, setSelectedSubCategory] = useState(t('features.faq.label.all'));
  /** ソート選択 */
  const [sortKey, setSortKey] = useState<'update' | 'publish'>('update');
  /** ソート昇順フラグ */
  const [isAsc, setIsAsc] = useState<boolean>(false);
  /** 表示しているページ */
  const [currentPage, setCurrentPage] = useState(1);

  /** 1ページの一覧表示するQAの数 */
  const itemsPerPage = 5;
  /** ページ遷移ボタンの最大表示数 */
  const maxVisiblePages = 5

  // カテゴリ・サブカテゴリのドロップダウン選択肢を動的生成
  /** カテゴリ選択肢 */
  const categoryOptions = useMemo(() => {
    return [t('features.faq.label.all'), ...Array.from(new Set(faqData.map((item) => item.category.categoryName[lang]).filter((sub) => sub && sub.trim() !== '')))];
  }, [faqData]);
  /** サブカテゴリ選択肢 */
  const subCategoryOptions = useMemo(() => {
    // 1. カテゴリが「すべて」の場合は空配列を返す
    if (selectedCategory === t('features.faq.label.all')) {
      return [];
    }

    // 2. 選択されたカテゴリで絞り込み
    const filtered = faqData.filter(
      (item) => item.category.categoryName[lang] === selectedCategory
    );

    // 3. サブカテゴリを抽出し、空文字（"" や null/undefined、空白文字）を除外
    const validSubCategories = filtered
      .map((item) => item.category.subCategory[lang])
      .filter((sub) => sub && sub.trim() !== '');

    // 4. 重複を除去して先頭に「すべて」を追加
    return [
      t('features.faq.label.all'),
      ...Array.from(new Set(validSubCategories)),
    ];
  }, [faqData, selectedCategory]);

  /** 検索・絞り込み処理 */
  const filteredData = useMemo(() => {
    return faqData.filter((item) => {
      const matchQuery =
        !searchQuery ||
        item.question[lang].includes(searchQuery) ||
        item.answer[lang].includes(searchQuery);
      const matchCategory = selectedCategory === t('features.faq.label.all') || item.category.categoryName[lang] === selectedCategory;
      const matchSubCategory = selectedSubCategory === t('features.faq.label.all') || item.category.subCategory[lang] === selectedSubCategory;

      return matchQuery && matchCategory && matchSubCategory;
    });
  }, [faqData, searchQuery, selectedCategory, selectedSubCategory]);

  /**
   * ソート処理
   */
  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      // 判定対象の日付文字列を取得
      const dateA = sortKey === 'publish' ? a.publishDate : a.updateDate;
      const dateB = sortKey === 'publish' ? b.publishDate : b.updateDate;

      // isAsc が true なら昇順（古い順）、false なら降順（新しい順）
      return isAsc ? dateA.localeCompare(dateB) : dateB.localeCompare(dateA);
    });
  }, [filteredData, sortKey, isAsc]);

  // ページネーション計算
  /** 表示されるFAQのページ数 */
  const totalPages = Math.ceil(sortedData.length / itemsPerPage) || 1;
  /**
   * 表示するFAQ一覧をページごとに.slice()で切り分ける
   * @return 指定ページの一覧FAQ
   */
  const currentItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(start, start + itemsPerPage);
  }, [sortedData, currentPage]);

  /**
   * 表示するページ番号（最大5個）の動的計算
   */
  const pageNumbers = useMemo(() => {
    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    let start = currentPage - Math.floor(maxVisiblePages / 2);
    let end = start + maxVisiblePages - 1;

    if (start < 1) {
      start = 1;
      end = maxVisiblePages;
    } else if (end > totalPages) {
      end = totalPages;
      start = totalPages - maxVisiblePages + 1;
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [currentPage, totalPages, maxVisiblePages]);

  /**
   * 絞り込み変更時に1ページ目へ戻す
   */
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, selectedSubCategory, sortKey]);

  /**
   * ページ最上部へスムーズスクロール
   */
  const handleScrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

/**
 * 回答テキスト内の [テキスト](URL) を解析して Link コンポーネントに変換する内部関数
 */
const formatAnswerText = (text: string): React.ReactNode => {
  if (!text) return null;

  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  linkRegex.lastIndex = 0;

  // テキスト全体から [表示テキスト](URL) を抽出
  while ((match = linkRegex.exec(text)) !== null) {
    // リンクより手前の通常テキストを追加
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    const label = match[1];
    const url = match[2];
    const isExternal = url.startsWith('http');

    // 表示テキスト自体がパス名やURLの形式であるかを判定
    const isLabelPath =
      label.startsWith('http://') ||
      label.startsWith('https://') ||
      label.startsWith('/') ||
      label === url;

    const showExternalIcon = isExternal && !isLabelPath;

    // リンク要素の追加
    parts.push(
      <ChakraLink
        key={match.index}
        href={url}
        isExternal={isExternal}
        color="blue.600"
        fontWeight="medium"
        textDecoration="underline"
        _hover={{ color: 'blue.800' }}
        mx="2px"
      >
        {label}
        {showExternalIcon && <ExternalLinkIcon mx="2px" mb="2px" />}
      </ChakraLink>
    );

    lastIndex = linkRegex.lastIndex;
  }

  // 最後のリンクより後ろの通常テキストを追加
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return (
    <Text whiteSpace="pre-wrap" color="#333" lineHeight="1.6">
      <Text as="span" fontWeight="bold" mr={1.5} color="#333">
        A.
      </Text>
      {parts.length > 0 ? parts : text}
    </Text>
  );
};

  return (
    <Flex p={6} align="stretch" minH="100vh">
      {/* ページ上部へ戻るボタン */}
      <Flex align="flex-end" w="60px" flexShrink={0}>
        <Box>  
          <Tooltip label={t('features.faq.label.scrollToTop')}>
            <IconButton
              icon={<BsChevronDoubleUp />}
              aria-label={t('features.faq.label.scrollToTop')}
              onClick={handleScrollToTop}
              colorScheme="blue"
              variant="outline"
              borderRadius="full"
              size="lg"
              _hover={{ bg: 'blue.50', cursor: 'pointer' }}
            />
          </Tooltip>
        </Box>
      </Flex>
      {/* FAQ画面 */}
      <Box flex="1">
        <Container maxW="800px" my={8} p={6} bg="white" borderRadius="8px" boxShadow="0 2px 8px rgba(0,0,0,0.08)">
          {/* タイトルエリア */}
          <Heading
            as="h1"
            fontSize="25px"
            pb={2}
            mb={5}
            borderBottom="2px solid #0066cc"
            color="#1a1a1a"
          >
            <Flex align="center" gap={2}>
                <RiQuestionnaireLine size={30} color="#2B6CB0" />
                <Text fontWeight="bold">{t('features.faq.label.title')}</Text>
            </Flex>
          </Heading>
          {/* 検索エリア */}
          <HStack spacing={2} mb={5}>
            <DebouncedInput
                placeholder={t('features.faq.label.serch')}
                value={searchQuery}
                onChange={setSearchQuery}
                size="md"
                borderColor="gray.300"
            />
          </HStack>

          {/* コントロールエリア（絞り込み・並べ替え） */}
          <Flex
            bg="#f0f4f8"
            p={3}
            borderRadius="6px"
            mb={6}
            fontSize="14px"
            justify="center"
            align="center"
            flexWrap="wrap"
            gap={3}
          >
            <VStack align="stretch" spacing={3}>
              <HStack spacing={20}>
                {/* カテゴリ選択 */}
                <HStack spacing={1}>
                  <Text whiteSpace="nowrap">{t('features.faq.label.category')}:</Text>
                  <Select
                    size="sm"
                    bg="white"
                    w="fit-content"
                    value={selectedCategory}
                    onChange={(e) => {
                      setSelectedCategory(e.target.value);
                      setSelectedSubCategory(t('features.faq.label.all'));
                    }}
                  >
                    {categoryOptions.map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </Select>
                </HStack>
                {/* 並び順 */}
                <HStack spacing={1}>
                  <Text whiteSpace="nowrap">{t('features.faq.label.sortBy')}:</Text>
                  <Select
                    size="sm"
                    bg="white"
                    w="fit-content"
                    value={sortKey}
                    onChange={(e) => setSortKey(e.target.value as any)}
                  >
                    <option value="update">{t('features.faq.label.updatedDate')}</option>
                    <option value="publish">{t('features.faq.label.publishedDate')}</option>
                  </Select>
                  {/* 昇順降順切り替えアイコン */}
                  <IconButton
                    h="16px"
                    onClick={() => setIsAsc((prev) => !prev)}
                    _hover={{ bg: "gray.100", cursor: "pointer" }}
                    icon={isAsc ? <FaSortAmountUp /> : <FaSortAmountDown />}
                    aria-label={t('common.label.sort')}
                  />
                </HStack>
              </HStack>
              {/* サブカテゴリ選択 */}
              {selectedCategory !== t('features.faq.label.all') && ( // カテゴリですべて以外を選択したときのみ表示
                <HStack spacing={1}>
                  <Text whiteSpace="nowrap">{t('features.faq.label.subCategory')}:</Text>
                  <Select
                    size="sm"
                    bg="white"
                    w="fit-content"
                    value={selectedSubCategory}
                    onChange={(e) => setSelectedSubCategory(e.target.value)}
                  >
                    {subCategoryOptions.map((u) => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </Select>
                </HStack>
              )}
            </VStack>
          </Flex>

          {/* 検索結果件数 */}
          {sortedData.length > 0 && ( // 1件以上の時のみ表示
            <Stack spacing={4} mb={6}>
              <Box textStyle="center" color="gray.500">
                <Text>{sortedData.length} {t('features.faq.label.faqFound')}</Text>
              </Box>
            </Stack>
          )}

          {/* Q&A一覧（常時展開スタイル） */}
          <Stack spacing={4} mb={6}>
            {currentItems.length === 0 ? ( // 表示するFAQが一つもない場合、メッセージ表示
              <Box textStyle="center" py={8} color="gray.500" >
                {t('features.faq.label.noResult')}
              </Box>
            ) : (
              currentItems.map((item) => (
                <Card
                  key={item.id}
                  variant="outline"
                  borderColor="#e2e8f0"
                  borderRadius="6px"
                  bg="white"
                >
                  <CardBody p={4}>
                    {/* カテゴリ、掲載日、更新日 */}
                    <HStack fontSize="12px" color="#666" mb={2} spacing={3}>
                      <Text>{t('features.faq.label.category')}: {item.category.categoryName[lang] === "" ? "---" : item.category.categoryName[lang]}</Text>
                      <Text>{t('features.faq.label.publishedDate')}: {item.publishDate}</Text>
                      {/* 掲載日と更新日が一致している（更新されたことのない）場合、更新日を表示しない */}
                      {item.publishDate !== item.updateDate && (<Text>{t('features.faq.label.updatedDate')}: {item.updateDate}</Text>)}
                    </HStack>

                    {/* 質問 */}
                    <Text fontWeight="bold" fontSize="xl" color="#0066cc" mb={2.5}>
                      Q. {item.question[lang]}
                    </Text>

                    {/* 回答 */}
                    <Box
                      bg="#fafafa"
                      p={3}
                      borderRadius="4px"
                      borderLeft="4px solid #0066cc"
                      fontSize="14px"
                      lineHeight="1.6"
                      color="#333"
                      whiteSpace="pre-wrap"
                    >
                      {formatAnswerText(item.answer[lang])}
                    </Box>
                  </CardBody>
                </Card>
              ))
            )}
          </Stack>
          
          {/* ページネーションエリア */}
          {totalPages > 1 && ( // ページが1ページ以上ある場合、ページ遷移ボタンを表示する
            <HStack justify="center" spacing={1.5}>
              {totalPages > maxVisiblePages && ( // ページ数が表示されるボタンの数を上回った場合、最初のページ飛ばすボタンを表示
                <Button
                  size="sm"
                  isDisabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                >
                  {t('features.faq.label.firstPage')}
                </Button>
              )}
              <Button
                size="sm"
                isDisabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
              >
                {t('common.button.previous')}
              </Button>
              {/* 左側にまだページがある場合、"・・"でページがあることを示す */}
              {totalPages > (maxVisiblePages + 1) && currentPage > (maxVisiblePages/2 + 1) && (<Text>‥</Text>)}
              {pageNumbers.map((page) => (
                <Button
                  key={page}
                  size="sm"
                  variant={currentPage === page ? 'solid' : 'outline'}
                  colorScheme={currentPage === page ? "blue" : "gray"}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </Button>
              ))}
              {/* 右側にまだページがある場合、"・・"でページがあることを示す */}
              {maxVisiblePages < totalPages && currentPage < (totalPages - maxVisiblePages/2) && (<Text>‥</Text>)}
              <Button
                size="sm"
                isDisabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
              >
                {t('common.button.next')}
              </Button>
              {totalPages > maxVisiblePages && ( //　最後のページが表示されるボタンの数を上回った時、最後のページに飛ぶボタンを表示
                <Button
                  size="sm"
                  isDisabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(totalPages)}
                >
                  {t('features.faq.label.lastPage')}
                </Button>
              )}
            </HStack>
          )}
        </Container>
      </Box>
    </Flex>
  );
};

export default Faq;